import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostBinding, Input } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Observable } from 'rxjs';
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate.js';
import { ScrollContainerComponent } from '../../common/components/scroll.container/scroll.container.component';
import { FilterObject } from '../../common/filter';
import {
  ListOption,
  ScrollableListComponentBase,
} from '../../common/list.component.base';
import { DELETED, WebUtils } from '../../common/utils';
import { XlsUtils } from '../../common/xls.utils';
import { AccessUtils, TLocation, TOrganization } from '../../core/entities';
import { SiteLangMixin } from '../../core/lang';
import { SitePathMixin } from '../../core/site.path.utils';
import { SessionService } from '../../session.service';
import { SiteFormatMixin } from '../../thinkdesks/site.format.mixin';
import { LocationEditDialogComponent } from './location.edit.dialog';

class _Base extends ScrollableListComponentBase<TLocation> { }

@Component({
  selector: 'app-location-list',
  templateUrl: './location.list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ScrollContainerComponent,
    FontAwesomeModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatDialogModule,
    MatToolbarModule,
  ],
})
export class LocationListComponent extends SiteLangMixin(
  SitePathMixin(SiteFormatMixin(_Base))
) {
  _common = _common;
  @HostBinding('class') css = 'max-height';

  filterObject = new FilterObject();
  //   status = new FilterMulti();

  //   type = new FilterMulti();

  displayedColumns: string[] = [
    'image',
    'name',
    //'organization',
    'numSpaces_active',
    'numSpaces_total',
    //'spaces',
    'controls',
  ];

  @Input()
  organization: TOrganization;

  constructor(
    override http: HttpClient,
    override elRef: ElementRef,
    private dialog: MatDialog,
    override snackBar: MatSnackBar,
    public override session: SessionService
  ) {
    super(http, snackBar, elRef);
    // this.pageSort = 'seq';
    // this.pageSortDir = 'asc';
  }

  createBaseQuery() {
    let query = {
      organization: WebUtils.objId(this.organization),
    } as any;
    Object.assign(query, this.filterObject.query());
    Object.assign(query, this.session.userQuery());
    return query;
  }

  override createRequest(options: ListOption): Observable<Object> {
    let query = this.createBaseQuery();

    const url = this._listModifyUrl(`/api/location/list`, {}, options);
    return this.http.post(url, query);
  }

  override listFixList(list: TLocation[]) {
    for (let o of list) AccessUtils.fixLocation(o);
  }

  override selectObject(o: TLocation) {
    const dialogRef = this.dialog.open(LocationEditDialogComponent, {
      data: {
        objectId: o.id,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result == DELETED) {
        this._removeFromList(o);
      } else if (result) {
        Object.assign(o, result);
      }
    });
  }

  exportList() {
    let query = this.createBaseQuery();

    WebUtils.web_list(this.http.post(`/api/location/list`, query), (result) => {
      let list = result as any[];
      for (let o of list) {
        AccessUtils.fixLocation(o);
      }
      this.doExport(list);
    });
  }

  doExport(dataList: any[]) {
    XlsxPopulate.fromBlankAsync().then((workbook: XlsxPopulate.Workbook) => {
      const sheet = workbook.sheet('Sheet1');
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
      this.exportSheet(sheet, dataList);
      XlsUtils.webExport(workbook, null, {
        filename: `Center_list_${dateStr}.xlsx`,
      });
    });
  }

  exportSheet(sheet: XlsxPopulate.Sheet, dataList: any) {
    let columnOrder = [
      {
        path: 'createTime',
        name: 'Created at',
      },
      {
        path: 'active',
        name: 'Active',
      },
      {
        path: 'name',
        name: 'Center Name (zh_hk)',
      },
      {
        path: 'name_lang.en',
        name: 'Center Name (en)',
      },
      {
        path: 'organization.name',
        name: 'Organization (zh_hk)',
      },
      {
        path: 'organization.name_lang.en',
        name: 'Organization (en)',
      },
      {
        path: 'description',
        name: 'Description',
      },
      {
        path: 'area',
        name: 'Area',
      },
      {
        path: 'district',
        name: 'District',
      },
      {
        path: 'address',
        name: 'Address',
      },
      {
        path: 'tel',
        name: 'Tel',
      },
      {
        path: 'email',
        name: 'Email',
      },
      {
        path: 'contactPerson',
        name: 'Contact Person',
      },
      {
        path: 'emailRecipients',
        name: 'Email Recipients',
      },
      {
        path: 'openingHours',
        name: 'Opening Hours',
      },
      {
        path: 'terms',
        name: 'Booking Terms (Center)',
      },
      {
        path: 'facilities',
        name: 'Facilities',
      },
      {
        path: 'numSpaces',
        name: 'Number of Active Spaces',
      },
      {
        path: 'locked',
        name: 'Active Space with Smart Lock',
      },
      {
        path: 'unlocked',
        name: 'Active Space without Smart Lock',
      },
    ];

    const header = columnOrder.map((column) => column.name);

    let row = 1;
    sheet.cell(row++, 1).value([header]);

    for (const location of dataList) {
      const data = columnOrder.map((column) => {
        const columnKeys = column.path.split('.');
        let cell = location;

        switch (column.path) {
          case 'active':
            return cell.active ? 'Y' : 'N';
          case 'facilities':
            return cell.facilities
              .map((t) => this.lang(t.name, t.name_lang))
              .join(', ');
          case 'contactPerson':
            return this.lang(cell.contactPerson, cell.contactPerson_lang);
          case 'openingHours':
            return this.lang(
              cell.openingHours,
              cell.openingHours_lang
            ).replaceAll('\n', '\r\n');
          case 'area':
          case 'district':
            {

              let v = cell[column.path]
              return _common[v] ?? v
            }

          case 'address':
            return this.lang(cell.address, cell.address_lang);
          case 'description':
            return this.lang(cell.description, cell.description_lang);
          case 'createTime':
            return this.formatTime(cell.createTime);
        }
        for (const columnKey of columnKeys) {
          if (!cell[columnKey]) return '';
          else cell = cell[columnKey];
        }
        return cell;
      });
      let cc = sheet.cell(row, 1);
      cc.value([data]);
      sheet.cell(row, 10).style({ wrapText: true });
      row++;
      // cells.push(data);
    }

    // sheet.cell('A1').value(cells);
  }
}
